import type { Directives } from 'mdast-util-directive';
import { useCallback, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import { $isDirectiveNode, DirectiveEditorProps } from '@mdxeditor/editor';
import stylex from '@stylexjs/stylex';
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';

import type { IIframeMdxDirectiveAttributes } from './IframeMdxDirectiveToolbarButton';

const styles = stylex.create({
  frame: {
    pointerEvents: 'none',
  },
  frame$selected: {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'blue',
  },
});

// Inspired from:
// https://github.com/mdx-editor/editor/blob/main/src/plugins/image/ImageEditor.tsx

export const IframeMdxDirectiveEditor: React.ComponentType<
  DirectiveEditorProps<Directives>
> = (props) => {
  const { lexicalNode, mdastNode } = props;
  const attributes = mdastNode.attributes as IIframeMdxDirectiveAttributes;
  const nodeKey = lexicalNode.getKey();
  const [editor] = useLexicalComposerContext();
  const [selected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const editorRef = useRef<HTMLParagraphElement>(null);

  const handleDelete = useCallback(
    (payload: KeyboardEvent): boolean => {
      if (selected && $isNodeSelection($getSelection())) {
        const event: KeyboardEvent = payload;
        event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isDirectiveNode(node)) {
          node.remove();
        }
        return true;
      }

      return false;
    },
    [nodeKey, selected],
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand<MouseEvent>(
        CLICK_COMMAND,
        (event) => {
          if (event.target === editorRef.current) {
            if (event.shiftKey) {
              setSelected(!selected);
            } else {
              clearSelection();
              setSelected(true);
            }

            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        handleDelete,
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        handleDelete,
        COMMAND_PRIORITY_LOW,
      ),
    );

    return () => {
      unregister();
    };
  }, [editor, handleDelete, selected, setSelected, clearSelection]);

  return (
    <p ref={editorRef}>
      <iframe
        {...stylex.props(styles.frame, selected && styles.frame$selected)}
        src={attributes.url}
        title="Embedded page"
      ></iframe>
    </p>
  );
};
